/**
 * Timesheet dashboard — admin's deep view into hours logged.
 *
 * Split out of AdminDashboardPage at client request. Admin dashboard now
 * owns leaves + team-progress; this page owns everything hours-related.
 *
 * Controls:
 *   • Granularity: Week / Month / Year / Custom (with from/to inputs)
 *   • Employee selector: All employees (default) OR one specific member
 *
 * Body:
 *   • All employees: top metrics + projects breakdown + people×projects
 *     matrix with OT column (lifted from the old AdminDashboardPage)
 *   • Single employee: per-employee Total/Billable/Regular/OT cards
 *     plus the read-only Mon→Sun grid for the chosen range
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";

import { Avatar } from "@m2s/design-system";

import { DesktopOnly } from "@/components/DesktopOnly";
import { EmployeePicker } from "@/components/EmployeePicker";
import { SubmitOnBehalfModal } from "@/components/SubmitOnBehalfModal";
import { TimesheetGridView } from "@/components/TimesheetGridView";
import { api, downloadBlob, extractErrorMessage } from "@/lib/api";
import type {
  AdminSubmitResult,
  AdminTeamTimesheet,
  EmployeeTimesheetView,
  Member,
} from "@/lib/types";
import { useAuthStore } from "@/store/auth";

const ADMIN_ROLES = new Set(["admin"]);

// Longest range an admin can submit and approve at once (a month). Mirrors
// MAX_ADMIN_SUBMIT_DAYS in backend/app/api/v1/admin.py.
const MAX_SUBMIT_RANGE_DAYS = 31;

type Granularity = "week" | "month" | "year" | "custom";

// ---------- date helpers ----------

function startOfWeek(d: Date): Date {
  // Monday-anchored to match the rest of the app.
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const delta = (out.getDay() + 6) % 7;
  out.setDate(out.getDate() - delta);
  return out;
}
function endOfWeek(d: Date): Date {
  const s = startOfWeek(d);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
}
function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}
function endOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 11, 31);
}
function fmtISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function parseISO(s: string): Date {
  // Local-midnight so it doesn't shift by tz.
  return new Date(s + "T00:00:00");
}
function fmtWeekLabel(d: Date): string {
  const s = startOfWeek(d);
  const e = endOfWeek(d);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (s.getMonth() === e.getMonth()) {
    return `${s.toLocaleDateString(undefined, opts)} – ${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString(undefined, opts)} – ${e.toLocaleDateString(undefined, opts)}, ${e.getFullYear()}`;
}
function fmtMonthLabel(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function fmtYearLabel(d: Date): string {
  return String(d.getFullYear());
}
function hms(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}
function hoursStr(seconds: number): string {
  if (!seconds) return "0";
  return (seconds / 3600).toFixed(2);
}
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ---------- page ----------

export function TimesheetDashboardPage() {
  return (
    <DesktopOnly
      title="The timesheet dashboard"
      blurb="The team-wide hours grid and per-employee breakdown need more horizontal room than a phone screen offers."
    >
      <TimesheetDashboardImpl />
    </DesktopOnly>
  );
}

function TimesheetDashboardImpl() {
  const navigate = useNavigate();
  const memberships = useAuthStore((s) => s.memberships);
  const isAdmin = memberships.some((m) => ADMIN_ROLES.has(m.role));

  const [granularity, setGranularity] = useState<Granularity>("month");
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const today = useMemo(() => new Date(), []);
  const [customFrom, setCustomFrom] = useState<string>(() => fmtISO(startOfMonth(today)));
  const [customTo, setCustomTo] = useState<string>(() => fmtISO(endOfMonth(today)));
  const [userId, setUserId] = useState<"all" | string>("all");

  const { from, to } = useMemo(() => {
    if (granularity === "week") return { from: startOfWeek(anchor), to: endOfWeek(anchor) };
    if (granularity === "month") return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
    if (granularity === "year") return { from: startOfYear(anchor), to: endOfYear(anchor) };
    return { from: parseISO(customFrom), to: parseISO(customTo) };
  }, [granularity, anchor, customFrom, customTo]);

  const fromISO = fmtISO(from);
  const toISO = fmtISO(to);

  // Reset anchor to today when granularity changes (except custom).
  useEffect(() => {
    if (granularity !== "custom") setAnchor(new Date());
  }, [granularity]);

  // Member list — drives the dropdown and also gives us display names for
  // rows in the all-employees matrix.
  const membersQuery = useQuery({
    queryKey: ["members"],
    queryFn: async () => (await api.get<Member[]>("/admin/members")).data,
    enabled: isAdmin,
  });

  const teamQuery = useQuery({
    queryKey: ["admin", "team-timesheet", fromISO, toISO],
    queryFn: async () =>
      (
        await api.get<AdminTeamTimesheet>(
          `/admin/team-timesheet?from=${fromISO}&to=${toISO}`,
        )
      ).data,
    enabled: isAdmin && userId === "all",
  });

  const employeeQuery = useQuery({
    queryKey: ["admin", "employee-timesheet", userId, fromISO, toISO],
    queryFn: async () =>
      (
        await api.get<EmployeeTimesheetView>(
          `/admin/timesheets/employee/${userId}?from=${fromISO}&to=${toISO}`,
        )
      ).data,
    enabled: isAdmin && userId !== "all",
  });

  if (!isAdmin) {
    return (
      <div style={{ maxWidth: 480, margin: "120px auto", textAlign: "center" }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, margin: "0 0 12px" }}>Admin only</h1>
        <p style={{ color: "var(--body)", lineHeight: 1.6 }}>
          This dashboard is visible to workspace admins. Ask your admin if you need access.
        </p>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            marginTop: 16,
            background: "var(--ink)",
            color: "var(--on-primary)",
            border: "none",
            borderRadius: 4,
            padding: "8px 14px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Back to workspace
        </button>
      </div>
    );
  }

  const teamData = teamQuery.data;
  const empData = employeeQuery.data;

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Timesheet dashboard
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Hours logged across the team. Filter by employee and date range; toggle
          between Week, Month, Year, or a custom window.
        </p>
      </header>

      {/* Toolbar */}
      <div style={toolbar}>
        <div role="group" aria-label="Granularity" style={segmented}>
          {(["week", "month", "year", "custom"] as Granularity[]).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGranularity(g)}
              aria-pressed={granularity === g}
              style={segmentBtn(granularity === g)}
            >
              {g[0].toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>

        {granularity !== "custom" && (
          <>
            <button
              type="button"
              aria-label="Previous"
              onClick={() => {
                setAnchor((d) => {
                  if (granularity === "week") {
                    const n = new Date(d);
                    n.setDate(n.getDate() - 7);
                    return n;
                  }
                  if (granularity === "month") {
                    return new Date(d.getFullYear(), d.getMonth() - 1, 1);
                  }
                  return new Date(d.getFullYear() - 1, 0, 1);
                });
              }}
              style={navBtn}
            >
              ‹
            </button>
            <span style={dateLabel}>
              {granularity === "week" && fmtWeekLabel(anchor)}
              {granularity === "month" && fmtMonthLabel(anchor)}
              {granularity === "year" && fmtYearLabel(anchor)}
            </span>
            <button
              type="button"
              aria-label="Next"
              onClick={() => {
                setAnchor((d) => {
                  if (granularity === "week") {
                    const n = new Date(d);
                    n.setDate(n.getDate() + 7);
                    return n;
                  }
                  if (granularity === "month") {
                    return new Date(d.getFullYear(), d.getMonth() + 1, 1);
                  }
                  return new Date(d.getFullYear() + 1, 0, 1);
                });
              }}
              style={navBtn}
            >
              ›
            </button>
            <button type="button" onClick={() => setAnchor(new Date())} style={navBtn}>
              {granularity === "week" ? "This week" : granularity === "month" ? "This month" : "This year"}
            </button>
          </>
        )}

        {granularity === "custom" && (
          <>
            <span style={fieldHint}>From</span>
            <input
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              style={dateInput}
            />
            <span style={fieldHint}>To</span>
            <input
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              style={dateInput}
            />
          </>
        )}

        <span style={{ flex: 1 }} />

        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={fieldHint}>Employee</span>
          <EmployeePicker
            members={membersQuery.data ?? []}
            value={userId}
            onChange={setUserId}
            allowAll
          />
        </div>

        <button
          type="button"
          onClick={() => {
            if (userId === "all") teamQuery.refetch();
            else employeeQuery.refetch();
          }}
          style={navBtn}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Body — switches on the selected employee */}
      {userId === "all" ? (
        <AllEmployeesView data={teamData} loading={teamQuery.isLoading} />
      ) : (
        <SingleEmployeeView
          data={empData}
          loading={employeeQuery.isLoading}
          from={from}
          to={to}
        />
      )}
    </div>
  );
}

// ---------- all-employees view (lifted from old AdminDashboardPage) ----------

function AllEmployeesView({
  data,
  loading,
}: {
  data: AdminTeamTimesheet | undefined;
  loading: boolean;
}) {
  const cellByKey = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of data?.cells ?? []) m.set(`${c.user_id}::${c.project_id}`, c.seconds);
    return m;
  }, [data]);

  if (loading) return <p style={{ color: "var(--mute)" }}>Loading…</p>;
  if (!data) return <p style={{ color: "var(--mute)" }}>No data for this range.</p>;

  return (
    <>
      <div style={metricsRow}>
        <Metric label="Total logged" value={hms(data.grand_total_seconds)} accent="var(--ink)" />
        <Metric label="Billable" value={hms(data.grand_billable_seconds)} accent="var(--accent-green)" />
        <Metric
          label="Overtime (>8h/day)"
          value={hms(data.grand_overtime_seconds)}
          accent={data.grand_overtime_seconds > 0 ? "var(--accent-orange)" : "var(--mute)"}
        />
        <Metric label="Active users" value={String(data.users.length)} accent="var(--ink)" />
      </div>

      <section style={{ marginTop: 24 }}>
        <div style={sectionTitle}>By project</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
          }}
        >
          {data.projects.map((p) => {
            const pct = data.grand_total_seconds
              ? (p.total_seconds / data.grand_total_seconds) * 100
              : 0;
            return (
              <div key={p.project_id} style={projCard}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>{p.name}</span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "var(--font-mono)",
                      fontSize: 11,
                      color: "var(--mute)",
                    }}
                  >
                    {p.key}
                  </span>
                </div>
                <div style={{ fontSize: 22, fontWeight: 600, marginTop: 8 }}>
                  {hms(p.total_seconds)}
                </div>
                <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
                  {hms(p.billable_seconds)} billable
                </div>
                <div
                  aria-hidden
                  style={{
                    marginTop: 10,
                    height: 4,
                    borderRadius: 2,
                    background: "var(--surface-2)",
                    overflow: "hidden",
                  }}
                >
                  <div style={{ width: `${pct}%`, height: "100%", background: p.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section style={{ marginTop: 32 }}>
        <div style={sectionTitle}>People × projects</div>
        <div
          style={{
            border: "1px solid var(--hairline)",
            borderRadius: 4,
            overflow: "auto",
            background: "var(--canvas)",
          }}
        >
          <table style={matrixTable}>
            <thead>
              <tr style={{ background: "var(--surface-1)" }}>
                <th style={{ ...headCell, ...stickyLeft, width: 240, minWidth: 240 }}>Member</th>
                <th style={{ ...headCell, width: 80, textAlign: "center" }}>Role</th>
                {data.projects.map((p) => (
                  <th key={p.project_id} style={{ ...headCell, width: 96, textAlign: "center" }}>
                    <span
                      aria-hidden
                      style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: 2,
                        background: p.color,
                        marginRight: 6,
                      }}
                    />
                    {p.key}
                  </th>
                ))}
                <th style={{ ...headCell, width: 90, textAlign: "center" }}>Total</th>
                <th style={{ ...headCell, width: 80, textAlign: "center" }}>OT</th>
                <th style={{ ...headCell, width: 90, textAlign: "center" }}>Days</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.user_id}>
                  <td style={{ ...cellBase, ...stickyLeft, padding: "8px 10px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                      <Avatar initials={initialsOf(u.display_name)} title={u.display_name} />
                      <span>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{u.display_name}</div>
                        <div style={{ fontSize: 11, color: "var(--mute)" }}>{u.email}</div>
                      </span>
                    </span>
                  </td>
                  <td style={{ ...cellBase, textAlign: "center" }}>
                    <span style={rolePill(u.role)}>{u.role}</span>
                  </td>
                  {data.projects.map((p) => {
                    const secs = cellByKey.get(`${u.user_id}::${p.project_id}`) ?? 0;
                    return (
                      <td
                        key={p.project_id}
                        style={{
                          ...cellBase,
                          textAlign: "center",
                          fontFamily: "var(--font-mono)",
                          fontSize: 12,
                          color: secs ? "var(--ink)" : "var(--mute-soft)",
                        }}
                      >
                        {hoursStr(secs)}
                      </td>
                    );
                  })}
                  <td
                    style={{
                      ...cellBase,
                      textAlign: "center",
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    {hoursStr(u.total_seconds)}
                  </td>
                  <td
                    style={{
                      ...cellBase,
                      textAlign: "center",
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      color: u.overtime_seconds > 0 ? "var(--accent-orange)" : "var(--mute-soft)",
                    }}
                  >
                    {u.overtime_seconds > 0 ? hoursStr(u.overtime_seconds) : "—"}
                  </td>
                  <td
                    style={{
                      ...cellBase,
                      textAlign: "center",
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                      color: "var(--body-mid)",
                    }}
                  >
                    {u.days_logged}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: "var(--surface-1)", fontWeight: 600 }}>
                <td style={{ ...cellBase, ...stickyLeft, padding: "8px 10px", fontSize: 12 }}>Total</td>
                <td style={{ ...cellBase }} />
                {data.projects.map((p) => (
                  <td
                    key={p.project_id}
                    style={{
                      ...cellBase,
                      textAlign: "center",
                      fontFamily: "var(--font-mono)",
                      fontSize: 12,
                    }}
                  >
                    {hoursStr(p.total_seconds)}
                  </td>
                ))}
                <td
                  style={{
                    ...cellBase,
                    textAlign: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                  }}
                >
                  {hoursStr(data.grand_total_seconds)}
                </td>
                <td
                  style={{
                    ...cellBase,
                    textAlign: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    color:
                      data.grand_overtime_seconds > 0
                        ? "var(--accent-orange)"
                        : "var(--mute-soft)",
                  }}
                >
                  {data.grand_overtime_seconds > 0
                    ? hoursStr(data.grand_overtime_seconds)
                    : "—"}
                </td>
                <td style={{ ...cellBase }} />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    </>
  );
}

// ---------- single-employee view ----------

function SingleEmployeeView({
  data,
  loading,
  from,
  to,
}: {
  data: EmployeeTimesheetView | undefined;
  loading: boolean;
  from: Date;
  to: Date;
}) {
  const [dlError, setDlError] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  // Keyed by employee + range so the confirmation doesn't linger after the
  // admin switches to another employee or period.
  const rangeKey = `${data?.user_id ?? ""}|${fmtISO(from)}|${fmtISO(to)}`;
  const [submitNotice, setSubmitNotice] = useState<{ key: string; text: string } | null>(
    null,
  );
  const onDownload = async (fmt: "xlsx" | "pdf") => {
    if (!data) return;
    setDlError(null);
    const fromISO = fmtISO(from);
    const toISO = fmtISO(to);
    try {
      await downloadBlob(
        `/admin/timesheets/employee/${data.user_id}/export.${fmt}?from=${fromISO}&to=${toISO}`,
        `timesheet-${data.display_name}-${fromISO}.${fmt}`,
      );
    } catch (err) {
      setDlError(extractErrorMessage(err, `Could not download ${fmt.toUpperCase()}.`));
    }
  };
  if (loading) return <p style={{ color: "var(--mute)" }}>Loading…</p>;
  if (!data) return <p style={{ color: "var(--mute)" }}>No data for this employee.</p>;

  // Submit-and-approve eligibility. The dashboard range is what gets
  // submitted: the Week view is one Mon→Sun week, a Month walks each week.
  const weekStates = data.weeks ?? [];
  const approvedWeeks = weekStates.filter((w) => w.status === "approved").length;
  const pendingWeeks = weekStates.filter((w) => w.status === "submitted").length;
  const rejectedWeeks = weekStates.filter((w) => w.status === "rejected").length;
  const draftWeeks = weekStates.length - approvedWeeks - pendingWeeks - rejectedWeeks;
  const rangeDays = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  const rangeTooLong = rangeDays > MAX_SUBMIT_RANGE_DAYS;
  const allApproved = weekStates.length > 0 && approvedWeeks === weekStates.length;
  const submitDisabled = rangeTooLong || allApproved || weekStates.length === 0;
  const submitTitle = rangeTooLong
    ? `Choose a week or a month (up to ${MAX_SUBMIT_RANGE_DAYS} days) to submit.`
    : allApproved
      ? "Every week in this period is already approved."
      : "Submit and approve this timesheet on the employee's behalf";

  // The TimesheetGridView is sized for a single week. For Month/Year/Custom
  // ranges that span many days the table widens with horizontal scroll. The
  // component already handles overflow internally.
  return (
    <>
      <div style={empHeader}>
        <Avatar initials={initialsOf(data.display_name)} title={data.display_name} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{data.display_name}</div>
          <div style={{ fontSize: 12, color: "var(--mute)" }}>
            {data.email}
            {data.job_id ? (
              <>
                {" · "}
                <span style={{ fontFamily: "var(--font-mono)" }}>{data.job_id}</span>
              </>
            ) : null}
            {" · "}
            <span style={rolePill(data.role)}>{data.role}</span>
          </div>
        </div>
      </div>

      <div style={metricsRow}>
        <Metric label="Total logged" value={hms(data.total_seconds)} accent="var(--ink)" />
        <Metric
          label="Billable"
          value={hms(data.billable_seconds)}
          accent="var(--accent-green)"
        />
        <Metric
          label="Regular (≤8h/day)"
          value={hms(data.regular_seconds)}
          accent="var(--body)"
        />
        <Metric
          label="Overtime"
          value={hms(data.overtime_seconds)}
          accent={data.overtime_seconds > 0 ? "var(--accent-orange)" : "var(--mute)"}
        />
        <Metric label="Days logged" value={String(data.days_logged)} accent="var(--ink)" />
      </div>

      <section style={{ marginTop: 24 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div style={sectionTitle}>Breakdown</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setSubmitOpen(true)}
              disabled={submitDisabled}
              title={submitTitle}
              style={{
                ...submitBtn,
                opacity: submitDisabled ? 0.5 : 1,
                cursor: submitDisabled ? "not-allowed" : "pointer",
              }}
            >
              Submit and approve
            </button>
            <button
              type="button"
              onClick={() => onDownload("xlsx")}
              style={downloadBtn}
              title="Download Excel"
            >
              ⇣ Excel
            </button>
            <button
              type="button"
              onClick={() => onDownload("pdf")}
              style={downloadBtn}
              title="Download PDF"
            >
              ⇣ PDF
            </button>
          </div>
        </div>
        {dlError && (
          <p
            role="alert"
            style={{
              fontSize: 13,
              color: "#ee1d36",
              background: "rgba(238,29,54,0.06)",
              border: "1px solid rgba(238,29,54,0.3)",
              borderRadius: 4,
              padding: "8px 12px",
              marginBottom: 8,
            }}
          >
            {dlError}
          </p>
        )}
        {submitNotice && submitNotice.key === rangeKey && (
          <p role="status" style={noticeBox}>
            <span style={{ flex: 1 }}>{submitNotice.text}</span>
            <button
              type="button"
              onClick={() => setSubmitNotice(null)}
              aria-label="Dismiss"
              style={noticeDismiss}
            >
              Dismiss
            </button>
          </p>
        )}
        {weekStates.length > 0 && (
          <p style={{ fontSize: 12, color: "var(--mute)", margin: "0 0 8px" }}>
            {weekStates.length} week{weekStates.length === 1 ? "" : "s"} in this period:{" "}
            {approvedWeeks} approved, {pendingWeeks} awaiting approval, {rejectedWeeks} rejected,{" "}
            {draftWeeks} not submitted.
          </p>
        )}
        <TimesheetGridView
          from={from}
          to={to}
          rows={data.grid_rows}
          totalsByDay={data.totals_by_day}
          loading={false}
          readOnly
          onCellChange={() => {
            /* read-only */
          }}
        />
      </section>
      {submitOpen && (
        <SubmitOnBehalfModal
          employee={{ user_id: data.user_id, display_name: data.display_name }}
          from={fmtISO(from)}
          to={fmtISO(to)}
          weeks={weekStates}
          totalSeconds={data.total_seconds}
          billableSeconds={data.billable_seconds}
          onClose={() => setSubmitOpen(false)}
          onDone={(r: AdminSubmitResult) => {
            setSubmitOpen(false);
            const parts = [
              `Submitted and approved ${r.approved_weeks} week${r.approved_weeks === 1 ? "" : "s"} for ${data.display_name}.`,
            ];
            if (r.skipped_empty) parts.push(`${r.skipped_empty} with no hours skipped.`);
            if (r.skipped_locked) parts.push(`${r.skipped_locked} already approved.`);
            setSubmitNotice({ key: rangeKey, text: parts.join(" ") });
          }}
        />
      )}
    </>
  );
}

// ---------- atoms ----------

function Metric({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        background: "var(--canvas)",
        border: "1px solid var(--hairline)",
        borderRadius: 8,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: "var(--mute)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: "-0.3px",
          marginTop: 6,
          color: accent,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ---------- styles ----------

const toolbar: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  marginBottom: 16,
  flexWrap: "wrap",
};

const segmented: CSSProperties = {
  display: "inline-flex",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  overflow: "hidden",
  background: "var(--canvas)",
};

function segmentBtn(active: boolean): CSSProperties {
  return {
    border: "none",
    background: active ? "var(--ink)" : "transparent",
    color: active ? "var(--on-primary)" : "var(--body)",
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
  };
}

const downloadBtn: CSSProperties = {
  background: "var(--canvas)",
  color: "var(--ink)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

// Primary CTA — ink-black per the brand rules.
const submitBtn: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "1px solid var(--ink)",
  borderRadius: 4,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "var(--font-sans)",
};

const noticeBox: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  fontSize: 13,
  color: "var(--ink)",
  background: "var(--surface-1)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 12px",
  margin: "0 0 8px",
};

const noticeDismiss: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "var(--mute)",
  fontSize: 12,
  cursor: "pointer",
  padding: 0,
  fontFamily: "var(--font-sans)",
};

const navBtn: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "5px 10px",
  fontSize: 12,
  color: "var(--ink)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

const dateLabel: CSSProperties = {
  padding: "5px 14px",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  fontSize: 12.5,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const dateInput: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "4px 8px",
  fontSize: 12.5,
  fontFamily: "var(--font-mono)",
  color: "var(--ink)",
};

const fieldHint: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
};

const metricsRow: CSSProperties = {
  display: "flex",
  gap: 12,
  flexWrap: "wrap",
};

const sectionTitle: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 8,
};

const projCard: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 14,
};

const matrixTable: CSSProperties = {
  width: "max-content",
  minWidth: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const stickyLeft: CSSProperties = {
  position: "sticky",
  left: 0,
  background: "var(--canvas)",
  zIndex: 1,
  borderRight: "1px solid var(--hairline)",
};

const headCell: CSSProperties = {
  padding: "8px 10px",
  borderBottom: "1px solid var(--hairline)",
  fontWeight: 500,
  fontSize: 11,
  color: "var(--body-mid)",
  letterSpacing: 0.4,
  textTransform: "uppercase",
  textAlign: "left",
  background: "var(--surface-1)",
};

const cellBase: CSSProperties = {
  borderBottom: "1px solid var(--hairline)",
  height: 36,
  padding: "0 8px",
};

const empHeader: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 16,
  padding: 12,
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
};

function rolePill(role: string): CSSProperties {
  const highlight = role === "admin";
  return {
    fontSize: 11,
    padding: "2px 8px",
    borderRadius: 2,
    background: highlight ? "var(--ink)" : "var(--surface-2)",
    color: highlight ? "var(--on-primary)" : "var(--body-mid)",
    fontFamily: "var(--font-mono)",
    letterSpacing: 0.3,
    display: "inline-block",
  };
}
